import React, { useRef, useState } from 'react';

import { Divider, Popover, Typography } from '@mui/material';
import { SelectButton } from 'src/components';
import { InsertTableIcon } from 'src/components/icons/InsertTableIcon';
import { InsertTableDialog, TableSquares } from 'src/editor/toolbars';
import { ClientTransforms } from 'src/editor/transforms';
import { useCurrentUserId } from 'src/gql';
import { useAuthorizedTransforms } from 'src/hooks';
import { useEditor, useEditorLoading } from 'src/layout-engine';
import { useEditingMode } from 'src/store/document/selectors';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  insertButton: {
    width: '100%',
  },
  insertButtonText: {
    padding: theme.spacing(0.5, 0),
  },
  tableText: {
    marginLeft: theme.spacing(0.5),
  },
}));

export function InsertTableButton(): JSX.Element {
  const editor = useEditor();
  const { classes } = useStyles();
  const { editingMode } = useEditingMode();
  const currentUserId = useCurrentUserId();
  const editorLoading = useEditorLoading();
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const [popoverOpen, setPopoverOpen] = useState<boolean>(false);
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const { AuthorizedTransforms } = useAuthorizedTransforms();

  function insertTable(rowCount: number, columnCount: number): void {
    if (!editingMode || !currentUserId) return;

    AuthorizedTransforms.insertTable(editor, {
      authorId: currentUserId,
      editingMode,
      newTableProps: { columnCount, rowCount },
    });
    onClose();
    ClientTransforms.focus(editor);
  }

  function openInsertTableDialog(): void {
    setDialogOpen(true);
    onClose();
  }

  function onClose(): void {
    setPopoverOpen(false);
  }

  return (
    <>
      <SelectButton
        ref={buttonRef}
        disabled={editorLoading}
        onClick={() => setPopoverOpen(!popoverOpen)}
      >
        <InsertTableIcon />
        <Typography className={classes.tableText} color="inherit" variant="caption">
          Table
        </Typography>
      </SelectButton>
      <Popover
        anchorEl={buttonRef.current}
        anchorOrigin={{
          horizontal: 'left',
          vertical: 'bottom',
        }}
        open={popoverOpen}
        onClose={onClose}
      >
        <TableSquares insertTable={insertTable} />
        <Divider />
        <SelectButton className={classes.insertButton} onClick={openInsertTableDialog}>
          <Typography align="center" className={classes.insertButtonText}>
            Insert table...
          </Typography>
        </SelectButton>
      </Popover>
      <InsertTableDialog
        handleClose={() => setDialogOpen(false)}
        insertTable={insertTable}
        open={dialogOpen}
      />
    </>
  );
}
